'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'use-intl';

import { FEATURE_FLAGS, usePosthogFeatureFlag } from '@/shared-measurement';
import {
  SharedSurveyProgressTimer,
  useProjectResponseModeContext,
  parsePanel,
} from '@/shared-survey';
import { SurveyThemeProvider } from '@/shared-utilities';
import {
  InitiateSurveyV3Mutation,
  type GetProjectQuery,
} from '@/survey-graphql';

import { useAudioQueueController } from './project-response-audio-controller.hooks';
import ProjectChat from './project-response-chat';
import { getProjectChatProps } from './project-response-chat-props.utils';
import ProjectExpired from './project-response-expired';
import {
  useChannel,
  useResponderEmail,
} from './project-response-insight-info.hooks';
import ProjectPoweredBy from './project-response-powered-by';
import ProjectResponseProgressBar from './project-response-progress-bar';
import {
  DEFAULT_THEYSAID_LOGO_URL,
  isPollProject,
} from './project-response.helpers';
import { useProjectResponse } from './project-response.hooks';
import { useReportProblemSubmit } from './survey-project-response-report-problem.hooks';

interface Props {
  project: GetProjectQuery['project'];
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
}

export default function ProjectResponse({
  project,
  orgSettings,
  sessionId,
  firstQuestion,
}: Props) {
  const searchParams = useSearchParams();
  const tCommon = useTranslations('Common');

  const [conversationOverText, setConversationOverText] = useState<
    string | null
  >(null);

  const { responseMode } = useProjectResponseModeContext();
  const responderEmail = useResponderEmail();
  const channel = useChannel();
  const panel = parsePanel(searchParams);
  const { flag: reportProblemFlag } = usePosthogFeatureFlag(
    FEATURE_FLAGS.REPORT_PROBLEM_FEATURE
  );
  const {
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    handleSubmit,
    insightLoading,
    isConversationOver,
    progress,
    estimatedTotalTimeInSeconds,
    handleConversationOver,
    conversationOverRef,
    activityStore,
    insightId,
  } = useProjectResponse({
    project,
    responderEmail,
    channel,
    sessionId,
    firstQuestion,
    panel,
  });
  const handleReportProblemSubmit = useReportProblemSubmit({
    project,
    insightId,
    responderEmail,
  });

  useAudioQueueController(project?.isAiVoiceEnabled ?? false, responseMode);

  useEffect(() => {
    if (isConversationOver && conversationOverRef.current) {
      setConversationOverText(conversationOverRef.current);
    }
  }, [isConversationOver, conversationOverRef]);

  const themeValue = {
    themeSettings: project?.themeSettings,
    orgSettings: orgSettings ?? undefined,
  };

  const chatProps = getProjectChatProps({
    project,
    orgSettings,
    responseState: { isSaving, isSubmitting, isConversationOver },
  });

  if (!isPublished) {
    return <ProjectExpired showHomePageButton showPoweredBy />;
  }

  return (
    <SurveyThemeProvider value={themeValue}>
      <div
        data-test='project-response-root'
        className='flex min-h-dvh flex-col justify-between'
      >
        {/* Survey Header */}
        <div className='flex h-15 min-w-full items-center justify-between gap-4 p-4 sm:h-18.5 sm:px-6 sm:py-4'>
          <Image
            src={orgSettings?.logoUrl ?? DEFAULT_THEYSAID_LOGO_URL}
            alt={tCommon('logoAlt')}
            width={200}
            height={50}
            sizes='(min-width: 640px) 160px, 120px'
            data-default={!orgSettings?.logoUrl}
            data-test='project-response-logo'
            className='h-6 w-auto object-contain data-[default=true]:brightness-0 sm:h-8'
            loading='eager'
            quality={100}

            unoptimized
          />
          <div className='flex w-4/5 flex-col items-start justify-center gap-2'>
            <div
              data-test='project-response-title'
              className='w-full truncate text-sm font-semibold text-nowrap sm:text-center sm:text-base'
            >
              {project.publicTitle}
            </div>
            <div className='w-full sm:hidden'>
              <ProjectResponseProgressBar progress={progress} />
            </div>
          </div>
          {!isPollProject(project.type, project.isPoll) ? (
            <SharedSurveyProgressTimer
              estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
              progress={progress}
            />
          ) : (
            <div className='size-10' />
          )}
        </div>
        <ProjectChat
          {...chatProps}
          insightLoading={insightLoading}
          messages={messages}
          onSubmit={handleSubmit}
          isConversationOver={isConversationOver}
          onConversationOver={handleConversationOver}
          conversationOverText={conversationOverText}
          activityStore={activityStore}
          onReportProblemSubmit={
            reportProblemFlag(handleReportProblemSubmit) ?? undefined
          }
          classes={{
            root: 'flex flex-col flex-1 mx-4 sm:mx-6',
            container: 'max-w-180',
            formGradientOuter: 'max-w-180',
          }}
        />
        <ProjectPoweredBy />
      </div>
    </SurveyThemeProvider>
  );
}
